export const ENCOUNTER_HEARTBEAT_DISTANCE_M = 12;
export const ENCOUNTER_HEARTBEAT_MAX_INTERVAL_MS = 15000;
export const ENCOUNTER_DEBUG_GPS_GRACE_MS = 4500;
export const ENCOUNTER_ACTION_GPS_REFRESH_TIMEOUT_MS = 7000;
export const ENCOUNTER_ACTION_GPS_FRESH_AGE_MS = 30000;
export const ENCOUNTER_ACTION_GPS_MAX_AGE_MS = 120000;
export const ENCOUNTER_ACTION_GPS_MAX_ACCURACY_M = 50;

export function encounterSpawnNotice(payload, debugType = '') {
  if (payload?.encounter) return '';
  if (payload?.spawnPending) return 'Begegnung vorgemerkt · Der Wege-Dienst antwortet gerade nicht. Wir versuchen es automatisch erneut.';
  if (payload?.reason !== 'NO_SAFE_SPAWN') return '';
  if (debugType) return `Kein sicher erreichbarer Begegnungsort gefunden (${payload?.walkability?.reason || 'unbekannt'}).`;
  return 'Wir suchen beim Weitergehen einen erreichbaren Begegnungsort.';
}

export function normalizeEncounterDebugType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'verlorene-buddel') return 'verlorene_buddel';
  return ['treibgut', 'verlorene_buddel', 'pfandspur'].includes(normalized) ? normalized : '';
}

export function encounterVisualPoints(encounter) {
  const completedPresentation = encounter?.status === 'completed' && encounter?.payload?.presentation?.animation === 'success';
  if (!encounter || (encounter.status !== 'active' && !completedPresentation)) return [];
  if (encounter.type !== 'pfandspur') {
    return [{ key: encounter.type, type: encounter.type, ...encounter.position }];
  }
  const trail = encounter.payload?.trail || {};
  const points = Array.isArray(trail.points) ? trail.points : [];
  const progress = Math.max(0, Number(trail.progress || 0));
  const visibleAhead = Math.max(2, Math.min(4, Number(trail.visibleAhead || 4)));
  const visible = completedPresentation ? points.slice(-1) : points.slice(progress, progress + visibleAhead);
  return visible.map((point) => ({ ...point, key: `trail-${point.index}` }));
}

export function encounterTarget(encounter) {
  if (!encounter) return null;
  if (encounter.type !== 'pfandspur') return encounter.position || null;
  const trail = encounter.payload?.trail || {};
  const points = Array.isArray(trail.points) ? trail.points : [];
  return points[Math.max(0, Number(trail.progress || 0))] || null;
}

export function isEncounterActionInRange(encounter, distance) {
  return Number.isFinite(distance) && distance <= Number(encounter?.minActivationDistance || 15);
}

export function encounterGpsFixState(fix, now = Date.now()) {
  const latitude = Number(fix?.latitude ?? fix?.lat);
  const longitude = Number(fix?.longitude ?? fix?.lng);
  const timestamp = Number(fix?.receivedAt ?? fix?.timestamp);
  const accuracy = fix?.accuracy === null || fix?.accuracy === undefined ? Number.NaN : Number(fix.accuracy);
  const structurallyValid = fix?.source === 'gps'
    && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
    && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180
    && Number.isFinite(timestamp) && timestamp > 0;
  if (!structurallyValid) {
    return { usable: false, state: 'missing', ageMs: null, accuracy: Number.isFinite(accuracy) ? accuracy : null };
  }
  const ageMs = Number(now) - timestamp;
  if (!Number.isFinite(accuracy) || accuracy < 0 || accuracy > ENCOUNTER_ACTION_GPS_MAX_ACCURACY_M) {
    return { usable: false, state: 'inaccurate', ageMs, accuracy: Number.isFinite(accuracy) ? accuracy : null };
  }
  if (ageMs > ENCOUNTER_ACTION_GPS_MAX_AGE_MS || ageMs < -15000) {
    return { usable: false, state: 'stale', ageMs, accuracy };
  }
  return {
    usable: true,
    state: ageMs <= ENCOUNTER_ACTION_GPS_FRESH_AGE_MS ? 'fresh' : 'aging',
    ageMs,
    accuracy,
  };
}

export function waitForEncounterGpsFix({ getCurrentFix, subscribe, ensureActive, timeoutMs = ENCOUNTER_ACTION_GPS_REFRESH_TIMEOUT_MS, now = Date.now }) {
  const acceptable = (fix) => encounterGpsFixState(fix, now()).usable;
  const current = getCurrentFix();
  if (acceptable(current)) return Promise.resolve(current);
  ensureActive();

  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe = () => {};
    let timer = null;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      unsubscribe();
      callback(value);
    };
    unsubscribe = subscribe((fix) => {
      if (acceptable(fix)) finish(resolve, fix);
    });
    const afterSubscribe = getCurrentFix();
    if (acceptable(afterSubscribe)) {
      finish(resolve, afterSubscribe);
      return;
    }
    timer = setTimeout(() => {
      const locationError = new Error('Für diese Aktion wird ein aktueller, genauer GPS-Standort benötigt.');
      locationError.code = 'encounter_gps_timeout';
      locationError.gpsState = encounterGpsFixState(getCurrentFix(), now());
      finish(reject, locationError);
    }, Math.max(1, Number(timeoutMs) || ENCOUNTER_ACTION_GPS_REFRESH_TIMEOUT_MS));
  });
}

export function encounterActionErrorMessage(actionError) {
  const code = String(actionError?.payload?.code || actionError?.code || '');
  if (code === 'encounter_gps_missing') return 'Für diese Aktion wird ein aktueller GPS-Standort benötigt.';
  if (code === 'encounter_gps_stale') return 'Der GPS-Standort ist zu alt. Der Standort wird beim nächsten Versuch aktualisiert.';
  if (code === 'encounter_gps_inaccurate') return 'Das GPS-Signal ist zu ungenau. Bitte warte kurz auf ein besseres Signal.';
  if (code === 'encounter_too_far') return actionError?.message || 'Du bist noch nicht nah genug an der Begegnung.';
  return actionError?.message || 'Die Begegnungsaktion ist fehlgeschlagen.';
}

export function resolveEncounterVisualPosition(playerPosition, debugType, debugFallbackReady, fallbackPosition) {
  if (playerPosition) return playerPosition;
  if (debugType && debugFallbackReady) return fallbackPosition;
  return null;
}

export function shouldSendEncounterHeartbeat(previous, nextPosition, now, distanceMeters) {
  if (!nextPosition) return false;
  if (!previous) return true;
  const moved = Number(distanceMeters(previous.position, nextPosition));
  const elapsed = Number(now) - Number(previous.sentAt || 0);
  return moved >= ENCOUNTER_HEARTBEAT_DISTANCE_M || elapsed >= ENCOUNTER_HEARTBEAT_MAX_INTERVAL_MS;
}
